import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { 
  ArrowLeft, Trophy, Clock, Check, Gift, Star, Target, Skull,
  Zap, Shield, Flame, Timer, Award, TrendingUp, Calendar
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIFFICULTY_COLORS = {
  easy: { bg: '#22C55E20', border: '#22C55E', text: '#22C55E' },
  medium: { bg: '#F59E0B20', border: '#F59E0B', text: '#F59E0B' },
  hard: { bg: '#EF444420', border: '#EF4444', text: '#EF4444' },
  extreme: { bg: '#A855F720', border: '#A855F7', text: '#A855F7' }
};

const REWARD_ICONS = {
  xp: <Zap className="w-4 h-4" />,
  bonus_life: <Shield className="w-4 h-4" />,
  score_multiplier: <TrendingUp className="w-4 h-4" />
};

const CHALLENGE_TYPE_ICONS = {
  kills: <Skull className="w-5 h-5" />,
  kill_streak: <Flame className="w-5 h-5" />,
  score: <Star className="w-5 h-5" />,
  level: <TrendingUp className="w-5 h-5" />,
  survive_time: <Timer className="w-5 h-5" />,
  powerups: <Gift className="w-5 h-5" />,
  default: <Target className="w-5 h-5" />
};

const DailyChallenges = ({ onBack }) => {
  const { publicKey } = useWallet();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [resetTime, setResetTime] = useState(0);
  const [stats, setStats] = useState({ completed: 0, claimed: 0 });
  const [history, setHistory] = useState([]);
  const [streak, setStreak] = useState(0);
  const [rewards, setRewards] = useState({ xp: 0, bonus_life: 0, score_multiplier: 0 });

  const fetchChallenges = useCallback(async () => {
    try {
      setLoading(true);

      // Resolve a player id - wallet if connected, else persistent guest id
      const playerId = publicKey?.toBase58() || (() => {
        let g = sessionStorage.getItem('metal_lego_guest_id');
        if (!g) { g = 'guest_' + Math.random().toString(36).substr(2, 8); sessionStorage.setItem('metal_lego_guest_id', g); }
        return g;
      })();

      // Fetch challenges with player progress (works for guest IDs too)
      const response = await axios.get(`${API}/challenges/daily/${playerId}`);
      setChallenges(response.data.challenges || []);
      setResetTime(response.data.reset_in_seconds || 0);
      setStats({
        completed: response.data.completed_count || 0,
        claimed: response.data.claimed_count || 0
      });

      // Fetch history and rewards (only for wallet-connected players)
      if (publicKey) {
        const [historyRes, rewardsRes] = await Promise.all([
          axios.get(`${API}/challenges/history/${publicKey.toBase58()}`),
          axios.get(`${API}/challenges/rewards/${publicKey.toBase58()}`)
        ]);

        setHistory(historyRes.data.history || []);
        setStreak(historyRes.data.streak || 0);
        setRewards(rewardsRes.data.totals || {});
      } else {
        setHistory([]);
        setStreak(0);
        setRewards({ xp: 0, bonus_life: 0, score_multiplier: 0 });
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
      toast.error('Failed to load daily challenges');
    } finally {
      setLoading(false);
    }
  }, [publicKey]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  // Countdown timer
  useEffect(() => {
    if (resetTime <= 0) return;
    
    const interval = setInterval(() => {
      setResetTime(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [resetTime]);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '--:--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClaimReward = async (challengeId) => {
    if (!publicKey) {
      toast.error('Connect wallet to claim rewards');
      return;
    }
    
    setClaiming(challengeId);
    try {
      const response = await axios.post(`${API}/challenges/claim-reward`, {
        wallet_address: publicKey.toBase58(),
        challenge_id: challengeId
      });
      
      toast.success(response.data.message);
      
      // Refresh challenges
      fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    } finally {
      setClaiming(null);
    }
  };

  const getChallengeIcon = (type) => {
    return CHALLENGE_TYPE_ICONS[type] || CHALLENGE_TYPE_ICONS.default;
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-[#A1A1AA] hover:text-white"
            data-testid="challenges-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2 glass-card px-4 py-2">
            <Clock className="w-5 h-5 text-[#FF4444]" />
            <span className="font-game text-[#FF4444]" data-testid="reset-timer">
              {formatTime(resetTime)}
            </span>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="font-game text-3xl md:text-4xl text-[#FFD700] mb-2 flex items-center justify-center gap-3">
            <Calendar className="w-8 h-8" />
            DAILY CHALLENGES
          </h1>
          <p className="text-[#A1A1AA] text-sm">
            Complete challenges to earn XP, bonus lives, and score multipliers
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-card p-4 text-center">
            <div className="text-2xl font-game text-[#14F195]">{stats.completed}/5</div>
            <div className="text-xs text-[#A1A1AA]">Completed Today</div>
          </div>
          <div className="glass-card p-4 text-center">
            <div className="text-2xl font-game text-[#FFD700]">{streak}</div>
            <div className="text-xs text-[#A1A1AA]">Day Streak</div>
          </div>
          <div className="glass-card p-4 text-center">
            <div className="text-2xl font-game text-[#9945FF]">{rewards.xp || 0}</div>
            <div className="text-xs text-[#A1A1AA]">Total XP Earned</div>
          </div>
          <div className="glass-card p-4 text-center">
            <div className="text-2xl font-game text-[#5AC8FA]">{rewards.bonus_life || 0}</div>
            <div className="text-xs text-[#A1A1AA]">Bonus Lives</div>
          </div>
        </div>

        {/* Week History */}
        {history.length > 0 && (
          <div className="glass-card p-4 mb-6">
            <h3 className="font-game text-sm text-[#A1A1AA] mb-3">LAST 7 DAYS</h3>
            <div className="flex justify-between gap-2">
              {history.slice(0, 7).map((day, index) => (
                <div 
                  key={day.date}
                  className="flex-1 text-center"
                  data-testid={`history-day-${index}`}
                >
                  <div className={`w-full h-2 rounded-full mb-1 ${
                    day.completed === 5 ? 'bg-[#14F195]' :
                    day.completed > 0 ? 'bg-[#FFD700]' : 'bg-[#333]'
                  }`} />
                  <div className="text-[10px] text-[#666]">
                    {index === 0 ? 'Today' : `${index}d`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Challenges List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Loading challenges...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {challenges.map((challenge, index) => {
              const progress = challenge.current_progress || 0;
              const percentage = getProgressPercentage(progress, challenge.target);
              const diffColor = DIFFICULTY_COLORS[challenge.difficulty] || DIFFICULTY_COLORS.medium;
              
              return (
                <div
                  key={challenge.id}
                  className={`glass-card p-4 border-l-4 transition-all ${
                    challenge.completed ? 'border-[#14F195]' : 'border-[#333]'
                  } ${challenge.claimed ? 'opacity-60' : ''}`}
                  data-testid={`challenge-${challenge.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Challenge Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: diffColor.bg }}
                        >
                          {getChallengeIcon(challenge.type)}
                        </div>
                        <div>
                          <h3 className="font-game text-sm text-white flex items-center gap-2">
                            {challenge.name}
                            {challenge.completed && <Check className="w-4 h-4 text-[#14F195]" />}
                          </h3>
                          <p className="text-[#A1A1AA] text-xs">{challenge.description}</p>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span 
                            className="px-2 py-0.5 rounded text-[10px] font-game uppercase"
                            style={{ 
                              backgroundColor: diffColor.bg,
                              color: diffColor.text,
                              border: `1px solid ${diffColor.border}`
                            }}
                          >
                            {challenge.difficulty}
                          </span>
                          <span className="text-[#A1A1AA]">
                            {progress.toLocaleString()}/{challenge.target.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              challenge.completed ? 'bg-[#14F195]' : 'bg-[#FFD700]'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Reward */}
                    <div className="flex flex-col items-end gap-2">
                      <div 
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm"
                        style={{ backgroundColor: diffColor.bg }}
                      >
                        {REWARD_ICONS[challenge.reward_type] || <Gift className="w-4 h-4" />}
                        <span className="font-game" style={{ color: diffColor.text }}>
                          +{challenge.reward_amount}
                        </span>
                      </div>
                      
                      {challenge.completed && !challenge.claimed && publicKey && (
                        <Button
                          onClick={() => handleClaimReward(challenge.id)}
                          disabled={claiming === challenge.id}
                          className="bg-[#14F195] text-black hover:bg-[#14F195]/90 text-xs px-4 py-1"
                          data-testid={`claim-${challenge.id}`}
                        >
                          {claiming === challenge.id ? (
                            <span className="animate-pulse">CLAIMING...</span>
                          ) : (
                            <>
                              <Gift className="w-3 h-3 mr-1" />
                              CLAIM
                            </>
                          )}
                        </Button>
                      )}
                      
                      {challenge.claimed && (
                        <div className="text-[#14F195] text-xs font-game flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          CLAIMED
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div className="mt-8 glass-card p-4 text-center">
          <p className="text-[#A1A1AA] text-sm">
            <Trophy className="w-4 h-4 inline mr-2 text-[#FFD700]" />
            New challenges every day at <span className="text-[#FFD700] font-game">00:00 UTC</span>
          </p>
          <p className="text-[#666] text-xs mt-2">
            Challenge progress updates as you play. Connect your wallet to track progress and claim rewards.
          </p>
        </div>

        {/* Reward Types Legend */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="glass-card p-3 text-center">
            <Zap className="w-5 h-5 text-[#9945FF] mx-auto mb-1" />
            <div className="text-xs text-[#A1A1AA]">XP</div>
            <div className="text-[10px] text-[#666]">Experience points</div>
          </div>
          <div className="glass-card p-3 text-center">
            <Shield className="w-5 h-5 text-[#5AC8FA] mx-auto mb-1" />
            <div className="text-xs text-[#A1A1AA]">Bonus Life</div>
            <div className="text-[10px] text-[#666]">Extra life in game</div>
          </div>
          <div className="glass-card p-3 text-center">
            <TrendingUp className="w-5 h-5 text-[#14F195] mx-auto mb-1" />
            <div className="text-xs text-[#A1A1AA]">Score Multiplier</div>
            <div className="text-[10px] text-[#666]">2x points boost</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyChallenges;
